"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

/** 한 번에 그리는 행 수 — 스크롤이 센티넬에 닿을 때마다 이만큼 더 붙인다. */
export const WINDOW_STEP = 24;

/**
 * 첫 화면 미리보기 상한(10-15) — 모바일 드로어를 서버 HTML 에 미리 그릴 때(레이아웃 판정 전)
 * 목록을 이만큼만 그린다. 데스크톱 트리가 이미 24행을 서버 렌더하므로 같은 행을 다시 다
 * 싣지 않는다. 상한이 걸린 동안은 센티넬도 내지 않고(이어 붙이기 없음), 상한이 풀리면
 * 지금 창(count) 그대로 이어서 그린다. null = 상한 없음.
 */
const WindowCapContext = createContext<number | null>(null);
export const WindowCapProvider = WindowCapContext.Provider;

/**
 * 긴 목록을 창(window)으로 잘라 그린다.
 *
 * 허브의 지점·차종 목록은 수백 행이라 전부 서버 렌더하면 HTML 이 수 MB 가 된다.
 * 처음 24행만 그리고, 목록 끝에 둔 센티넬이 화면에 들어오면 24행씩 이어 붙인다.
 * 관측 root 는 viewport(null) — 모바일 드로어는 fixed 라 그 안의 센티넬도
 * viewport 기준으로 정확히 판정된다.
 *
 * setState 는 IntersectionObserver 콜백에서만 일어나고(효과 본문 setState 금지 규칙),
 * 목록 길이가 바뀌면(필터·시트 전환) 렌더 중 조정으로 처음부터 다시 센다.
 */
export function useWindowedCount(total: number, step: number = WINDOW_STEP) {
  const [count, setCount] = useState(() => Math.min(step, total));
  const [syncedTotal, setSyncedTotal] = useState(total);
  const [sentinel, setSentinel] = useState<HTMLDivElement | null>(null);

  if (syncedTotal !== total) {
    setSyncedTotal(total);
    setCount(Math.min(step, total));
  }

  const attachSentinel = useCallback(
    (node: HTMLDivElement | null) => setSentinel(node),
    [],
  );

  const cap = useContext(WindowCapContext);
  const windowed = Math.min(count, total);
  const visible = cap === null ? windowed : Math.min(windowed, cap);
  // 상한이 걸린 미리보기에서는 센티넬을 내지 않는다 — 상한이 풀린 뒤에 이어 붙인다.
  const hasMore = cap === null && visible < total;

  useEffect(() => {
    if (!sentinel || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setCount((current) => Math.min(current + step, total));
        }
      },
      { rootMargin: "300px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [sentinel, hasMore, step, total]);

  return { visible, hasMore, attachSentinel };
}
